import { recoverTerminalReturn, TERMINAL_RETURN_EVENT, type TerminalReturn } from './services/TerminalReturnService';
import { TerminalReturnScreen } from './components/screens/TerminalReturnScreen';
/**
 * Main App Component
 * Updated with DisplayModeProvider for standalone/embedded support
 * Includes session recovery detection and browser navigation resilience
 */

import { useState, useEffect, useCallback, Suspense } from 'react';
import { BrowserRouter } from 'react-router-dom';
import { ScreenFlowProvider } from './contexts/ScreenFlowContext';
import { DisplayModeProvider } from './components/layout/DisplayModeProvider';
import { VerificationProvider } from './contexts/VerificationContext';
import { ScreenRouter } from './components/ScreenRouter';
import { ErrorBoundary } from './components/shared';
import ErrorScreen from './components/screens/ErrorScreen';
import type { ScreenType } from './types/screens';
import { logger } from './utils/logger';
import {
  initializeSession,
  sessionStateManager,
  updateSessionState,
} from './utils/SessionStateManager';
import { AppInitializationService, type AppInitialState } from './services/AppInitializationService';
import {
  getDesktopObserverRecoveryProjection,
  isEmblemUpgradeProjection,
  isExpiredLifecycleError,
  isRateLimitedLifecycleError,
  verificationLifecycleService,
} from './services/VerificationLifecycleService';
import { runtimeConfig } from './services/RuntimeConfigService';
import { DevBrandProvider } from './contexts/DevBrandContext';
import { BrandProvider, useBrandContext } from './contexts/BrandContext';
import { BrandThemeProvider } from './contexts/BrandThemeProvider';
import { LanguageProvider, useLanguage } from './i18n';
import { SCREEN_PATHS } from './utils/screenPaths';
import { clearIntentionalNavigation } from './utils/navigationIntent';
import { faceToIdTransition } from './core/FaceToIdTransition';
import { removeLaunchCredentialsFromAddressBar } from './utils/launchCredentials';

const appLogger = logger.clone({ labels: { component: 'App' }});

const ID_FLOW_SCREENS: ReadonlySet<ScreenType> = new Set([
  'id-request',
  'id-capture',
  'id-review',
  'id-processing',
]);

// All initialization logic moved to AppInitializationService for KISS principles

/**
 * Component that sets brand from config after initialization
 * Must be inside BrandProvider to access setBrand
 */
const BrandInitializer: React.FC<{ config: AppInitialState['config'] }> = ({ config }) => {
  const { setBrand } = useBrandContext();

  useEffect(() => {
    if (config?.brand) {
      appLogger.info('Setting brand from session config', {
        labels: {
          productName: config.brand.productName,
          hasTheme: `${!!config.brand.theme}`,
        }
      });
      setBrand(config.brand);
    }
  }, [config, setBrand]);

  return null;
};

const App: React.FC = () => {
  const [terminalReturn, setTerminalReturn] = useState<TerminalReturn | null>(null);
  useEffect(() => {
    const listener = (event: Event) => setTerminalReturn((event as CustomEvent<TerminalReturn>).detail);
    window.addEventListener(TERMINAL_RETURN_EVENT, listener);
    return () => window.removeEventListener(TERMINAL_RETURN_EVENT, listener);
  }, []);
  const [appState, setAppState] = useState<AppInitialState>({
    initialized: false,
    initialScreen: 'loading' as ScreenType,
    config: null,
  });
  const [sessionInitialized, setSessionInitialized] = useState(false);
  const [showError, setShowError] = useState<
    | 'missing-return-url'
    | 'direct-access'
    | 'verification-unavailable'
    | 'expired-session'
    | 'subscription-required'
    | 'plan-limit-reached'
    | 'sandbox-limit-reached'
    | null
  >(null);
  const [errorPortalUrl, setErrorPortalUrl] = useState<string | undefined>(
    undefined
  );

  // NEW: Initialize app state using AppInitializationService
  // This determines the initial screen BEFORE rendering to maintain SPA architecture
  useEffect(() => {
    const initializeApp = async () => {
      // This app can be reopened repeatedly in the same browser tab during local testing.
      // Always discard cached runtime config before resolving the next session from the URL.
      runtimeConfig.reset();
      verificationLifecycleService.reset();
      if (await recoverTerminalReturn()) return;
      const initialState = await AppInitializationService.determineInitialState();

      if (
        !initialState.error &&
        initialState.sessionId &&
        initialState.config?.sessionToken
      ) {
        try {
          const lifecycle = await verificationLifecycleService.reconcile();
          initialState.initialScreen = verificationLifecycleService.screenFor(lifecycle);
          if (lifecycle.emblemUpgradeRequired === true || isEmblemUpgradeProjection(lifecycle)) {
            // Portal-owned upgrade intent survives reload and restoration at
            // any stage without the callback flag, so success keeps the
            // upgraded-pass enrolment offer.
            initialState.emblemUpgradeRequired = true;
          }
          initialState.returnUrl = lifecycle.destination.returnUrl || initialState.returnUrl;
          initialState.cancelUrl = lifecycle.destination.cancelUrl || initialState.cancelUrl;
          if (ID_FLOW_SCREENS.has(initialState.initialScreen)) {
            // Local handoff readiness may initialize only after Portal has
            // projected the authoritative ID-flow stage.
            faceToIdTransition.beginRestoredIdFlow(initialState.sessionId);
          }
        } catch (error) {
          const observerProjection = initialState.isMobileQR
            ? null
            : getDesktopObserverRecoveryProjection(error);

          if (observerProjection) {
            appLogger.info('Restoring desktop mobile-handoff observer', {
              labels: {
                sessionId: observerProjection.sessionId.substring(0, 8) + '...',
                lifecycleVersion: `${observerProjection.lifecycleVersion}`,
                actorRole: observerProjection.actor?.role || 'unknown',
              },
            });
            initialState.initialScreen = 'desktop-waiting';
            initialState.returnUrl =
              observerProjection.destination.returnUrl || initialState.returnUrl;
            initialState.cancelUrl =
              observerProjection.destination.cancelUrl || initialState.cancelUrl;
          } else if (isRateLimitedLifecycleError(error)) {
            // Preserve the initialized session context so the authoritative
            // recovery screen can honor the retry window and perform one
            // bounded status check.
            initialState.initialScreen = 'failure';
            initialState.error = undefined;
          } else {
            appLogger.error(
              'Unable to confirm verification state',
              error instanceof Error ? error : new Error(String(error)),
            );
            initialState.error = isExpiredLifecycleError(error)
              ? 'Session Expired'
              : 'Verification Unavailable';
          }
        }
      }


      appLogger.info('App initial state determined', {
        labels: {
          initialScreen: initialState.initialScreen,
          hasConfig: `${!!initialState.config}`,
          hasError: `${!!initialState.error}`,
          isMobileQR: `${initialState.isMobileQR ?? false}`,
          isSessionToken: `${initialState.isSessionToken ?? false}`,
        }
      });

      // Handle errors
      if (initialState.error) {
        if (await recoverTerminalReturn(initialState.sessionId)) return;
        clearIntentionalNavigation(initialState.sessionId);
        if (initialState.error === 'Missing return URL') {
          setShowError('missing-return-url');
        } else if (initialState.error === 'Session Expired') {
          setShowError('expired-session');
        } else if (initialState.error === 'Direct Access Not Allowed') {
          setShowError('direct-access');
        } else if (initialState.error === 'Verification Unavailable') {
          setShowError('verification-unavailable');
        } else if (
          initialState.error === 'subscription-required' ||
          initialState.error === 'plan-limit-reached' ||
          initialState.error === 'sandbox-limit-reached'
        ) {
          setShowError(initialState.error);
          setErrorPortalUrl(initialState.blockingPortalUrl);
        } else {
          setShowError('direct-access');
        }
        setAppState(initialState);
        return;
      }

      // Initialize session if we have the data
      if (initialState.sessionId) {
        clearIntentionalNavigation(initialState.sessionId);
        const restoredSessionState = sessionStateManager.getState();
        initializeSession(initialState.sessionId, {
          apiKey: initialState.apiKey || '',
          returnUrl: initialState.returnUrl || '',
          cancelUrl: initialState.cancelUrl,
          displayMode: 'standalone',
          verificationMode: initialState.config?.verificationMode,
          challengeAge: initialState.config?.challengeAge,
          identityVerificationEnabled:
            initialState.config?.faceMatchEnabled ?? true,
          sessionToken: initialState.config?.sessionToken,
          brand: initialState.config?.brand,
          externalUserId: initialState.externalUserId,
          sandboxMode:
            initialState.config?.features?.sandboxMode ??
            restoredSessionState?.config?.sandboxMode,
          emblemUpgradeRequired: initialState.emblemUpgradeRequired ?? false,
          isMobileQR: initialState.isMobileQR ?? restoredSessionState?.config?.isMobileQR ?? false,
        });

        // IMPORTANT: If we're starting at a screen other than 'main', update the session's screenFlow
        // to match the intended initialScreen (e.g., 'camera-access' for skip_intro)
        if (initialState.initialScreen !== 'main') {
          updateSessionState({
            screenFlow: {
              currentScreen: initialState.initialScreen,
              screenHistory: [initialState.initialScreen],
              screenData: {},
              progress: 0,
              completedSteps: [],
            },
          });
        }

        // The bearer now has its refresh-continuity copy in session storage,
        // so launch credentials leave the address bar (and with it history,
        // copied links and later request lines). This is a no-op unless that
        // copy is confirmed, so the only copy is never discarded.
        removeLaunchCredentialsFromAddressBar({
          sessionId: initialState.sessionId,
          sessionToken: initialState.config?.sessionToken,
        });
      }

      // AppInitializationService now handles session recovery directly

      // IMPORTANT: Set both state and sessionInitialized in the same render cycle
      // This ensures ScreenFlowProvider receives the correct initialScreen on first mount
      const initialPath = SCREEN_PATHS[initialState.initialScreen];
      if (initialPath && window.location.pathname !== initialPath) {
        const url = new URL(window.location.href);
        url.pathname = initialPath;
        window.history.replaceState({}, '', url.toString());
      }
      setAppState(initialState);
      setSessionInitialized(true);
    };

    initializeApp();
  }, []);

  const handleScreenChange = useCallback((from: ScreenType, to: ScreenType) => {
    appLogger.debug(`Screen changed: ${from} → ${to}`);
    if (to === 'failure') return;
    void verificationLifecycleService.alignToScreen(to).catch((error) => {
      appLogger.warn('Unable to confirm screen transition', {
        labels: { to, error: error instanceof Error ? error.message : String(error) },
      });
    });
  }, []);

  if (terminalReturn) return <TerminalReturnScreen result={terminalReturn} />;

  // Show error screen if there's a critical error
  if (showError) {
    const errorBrand = appState.config?.brand ?? null;

    return (
      <DevBrandProvider>
        <LanguageProvider>
          <BrandProvider initialBrand={errorBrand}>
            <BrandInitializer config={appState.config} />
            <BrandThemeProvider>
              <BrowserRouter>
                <DisplayModeProvider>
                  <div className="app-wrapper">
                    <div className="app-container">
                      <Suspense fallback={<LoadingFallback />}>
                        <ErrorScreen
                          errorType={showError}
                          portalUrl={errorPortalUrl}
                          onRetry={() => window.location.reload()}
                          onCancel={() => {
                            const destination =
                              appState.cancelUrl ||
                              appState.returnUrl ||
                              appState.config?.cancelUrl ||
                              appState.config?.returnUrl;
                            if (destination) {
                              window.location.assign(destination);
                              return;
                            }
                            if (window.history.length > 1) {
                              window.history.back();
                            } else {
                              window.close();
                            }
                          }}
                        />
                      </Suspense>
                    </div>
                  </div>
                </DisplayModeProvider>
              </BrowserRouter>
            </BrandThemeProvider>
          </BrandProvider>
        </LanguageProvider>
      </DevBrandProvider>
    );
  }

  // Don't render main app until initialization is complete
  if (!appState.initialized || !sessionInitialized) {
    return (
      <DevBrandProvider>
        <LanguageProvider>
          <BrandProvider>
            <BrandThemeProvider>
              <InitializingFallback />
            </BrandThemeProvider>
          </BrandProvider>
        </LanguageProvider>
      </DevBrandProvider>
    );
  }

  return (
    <DevBrandProvider>
      <LanguageProvider>
        <BrandProvider>
          <BrandInitializer config={appState.config} />
          <BrandThemeProvider>
            <ErrorBoundary>
              <BrowserRouter>
                <DisplayModeProvider>
                  <VerificationProvider>
                    <ScreenFlowProvider
                      initialScreen={appState.initialScreen || 'main'}
                      onScreenChange={handleScreenChange}
                      enableSessionPersistence={true}
                    >
                      <div className="app-wrapper">
                        <div className="app-container">
                          <ScreenRouter />
                        </div>
                      </div>
                    </ScreenFlowProvider>
                  </VerificationProvider>
                </DisplayModeProvider>
              </BrowserRouter>
            </ErrorBoundary>
          </BrandThemeProvider>
        </BrandProvider>
      </LanguageProvider>
    </DevBrandProvider>
  );
};

// Global styles moved to /src/styles/global.css for better performance and caching

// Simple loading fallback component
const LoadingFallback = () => {
  const { t } = useLanguage();

  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        backgroundColor: 'var(--verify-screen-bg)',
        color: 'var(--verify-text-primary)',
        fontSize: '16px',
      }}
    >
      {t('common.loading')}
    </div>
  );
};

const InitializingFallback = () => {
  const { t } = useLanguage();

  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        backgroundColor: 'var(--verify-screen-bg)',
        color: 'var(--verify-text-primary)',
      }}
    >
      {t('common.initializing')}
    </div>
  );
};

export default App;
